import React, { useState, useEffect } from 'react';
import { Ticket } from '../types';
import { Button, Flex, Input, Text, Card } from './base-components';
import { useTranslation } from '../i18n/TranslationContext';
import { useManualTransfer } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { AxsTransferOtpPrompt } from './AxsTransferOtpPrompt';
import { ManualTransferOtpError } from '../api/client';

interface Props {
  tickets: Ticket[];
  open: boolean;
  onClose: () => void;
}

export const GiftTicketModal: React.FC<Props> = ({ tickets, open, onClose }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientFirstName, setRecipientFirstName] = useState('');
  const [recipientLastName, setRecipientLastName] = useState('');
  const [transferType, setTransferType] = useState<'email' | 'phone'>('email');
  const [isTransferring, setIsTransferring] = useState(false);
  /**
   * AXS now texts the seller a code and won't move anything until it comes
   * back. `params` is the body of the first call, kept as-is because the second
   * call is that same request plus the session and code.
   */
  const [otp, setOtp] = useState<{
    params: Parameters<ReturnType<typeof useManualTransfer>['mutateAsync']>[0];
    session: string;
    phoneNumber?: string;
    spent: boolean;
    error?: string;
  } | null>(null);
  const [otpResending, setOtpResending] = useState(false);

  const transferMutation = useManualTransfer();

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedTickets([]);
      setRecipientEmail('');
      setRecipientPhone('');
      setRecipientFirstName('');
      setRecipientLastName('');
      setTransferType('email');
      setIsTransferring(false);
      setOtp(null);
      setOtpResending(false);
    }
  }, [open]);

  const handleTicketToggle = (externalId: string) => {
    setSelectedTickets((prev) => {
      const newSelection = prev.includes(externalId)
        ? prev.filter((id) => id !== externalId)
        : [...prev, externalId];

      // If adding a ticket, check if it's compatible with existing selection
      if (!prev.includes(externalId) && newSelection.length > 1) {
        const firstTicket = tickets.find((t) => t.external_ticket_id === newSelection[0]);
        const newTicket = tickets.find((t) => t.external_ticket_id === externalId);

        if (firstTicket && newTicket) {
          // Check platform
          const firstPlatform = (firstTicket.external_platform_connection?.platform || firstTicket.platform || '').toUpperCase();
          const newPlatform = (newTicket.external_platform_connection?.platform || newTicket.platform || '').toUpperCase();

          if (firstPlatform !== newPlatform) {
            showError('All selected tickets must be from the same platform');
            return prev;
          }

          // Check event
          const firstEventId = typeof firstTicket.event === 'number' ? firstTicket.event : firstTicket.event?.id;
          const newEventId = typeof newTicket.event === 'number' ? newTicket.event : newTicket.event?.id;

          if (firstEventId !== newEventId) {
            showError('All selected tickets must be from the same event');
            return prev;
          }

          // Check same account (external_platform_connection.id)
          const firstAccountId = firstTicket.external_platform_connection?.id;
          const newAccountId = newTicket.external_platform_connection?.id;

          if (firstAccountId && newAccountId && firstAccountId !== newAccountId) {
            showError('All selected tickets must be from the same connected account');
            return prev;
          }

          // Check ticket type (optional - can be different types from same event)
          // Uncomment if you want to enforce same ticket type:
          // const firstTicketType = firstTicket.ticket_type || firstTicket.ticket_metadata?.type?.name;
          // const newTicketType = newTicket.ticket_type || newTicket.ticket_metadata?.type?.name;
          // if (firstTicketType && newTicketType && firstTicketType !== newTicketType) {
          //   showError('All selected tickets must be the same ticket type');
          //   return prev;
          // }
        }
      }

      return newSelection;
    });
  };

  const handleTransfer = async () => {
    if (selectedTickets.length === 0) return;

    const firstTicket = tickets.find((t) => t.external_ticket_id === selectedTickets[0]);
    if (!firstTicket) {
      showError('Selected ticket not found');
      return;
    }

    const platform = (firstTicket.external_platform_connection?.platform || firstTicket.platform || '').toUpperCase();
    const isDicePlatform = platform === 'DICE';
    const isAxsPlatform = platform === 'AXS';

    // Validate based on platform
    if (isDicePlatform) {
      if (transferType === 'email' && !recipientEmail) {
        showError('Please enter recipient email');
        return;
      }
      if (transferType === 'phone' && !recipientPhone) {
        showError('Please enter recipient phone number');
        return;
      }
    } else if (isAxsPlatform) {
      if (!recipientEmail) { showError('Please enter recipient email'); return; }
      if (!recipientFirstName) { showError('Please enter recipient first name'); return; }
      if (!recipientLastName) { showError('Please enter recipient last name'); return; }
    } else {
      if (!recipientEmail) {
        showError('Please enter recipient email');
        return;
      }
    }

    setIsTransferring(true);

    try {
      let eventId = '';
      if (typeof firstTicket.event === 'number') {
        eventId = String(firstTicket.event);
      } else if (firstTicket.event && typeof firstTicket.event === 'object') {
        eventId = String(firstTicket.event.id);
      }

      if (!eventId) {
        throw new Error('Event ID not found');
      }

      const params = {
        platform,
        eventId,
        ticketIds: selectedTickets,
        recipientEmail: (isDicePlatform && transferType === 'phone') ? undefined : (recipientEmail || undefined),
        recipientPhone: (isDicePlatform && transferType === 'phone') ? (recipientPhone || undefined) : undefined,
        recipientFirstName: isAxsPlatform ? (recipientFirstName || undefined) : undefined,
        recipientLastName: isAxsPlatform ? (recipientLastName || undefined) : undefined,
      };

      const result = await transferMutation.mutateAsync(params);

      // 202: the code is on its way and nothing has moved. Prompt, don't close.
      if (result.otp_required) {
        setOtp({ params, session: result.otp_session, phoneNumber: result.phone_number, spent: false });
        return;
      }

      showSuccess(result.message || `Successfully transferred ${selectedTickets.length} ticket(s)`);
      onClose();
    } catch (error: any) {
      showError(error?.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleOtpSubmit = async (code: string) => {
    if (!otp || isTransferring) return;
    setIsTransferring(true);
    try {
      const result = await transferMutation.mutateAsync({
        ...otp.params,
        otpSession: otp.session,
        otpCode: code,
      });
      if (result.otp_required) {
        setOtp({
          params: otp.params,
          session: result.otp_session,
          phoneNumber: result.phone_number ?? otp.phoneNumber,
          spent: false,
        });
        return;
      }
      showSuccess(result.message || `Successfully transferred ${selectedTickets.length} ticket(s)`);
      setOtp(null);
      onClose();
    } catch (error: any) {
      if (error instanceof ManualTransferOtpError && error.code === 'otp_session_invalid') {
        // Expired or already spent: there is nothing left to type into.
        setOtp(null);
        showError('That code expired. Start the transfer again.');
        return;
      }
      if (error instanceof ManualTransferOtpError) {
        // Wrong code burns the session too, so the next attempt needs a new one.
        setOtp({ ...otp, spent: true, error: 'That code wasn\u2019t right. Send a new code to try again.' });
        return;
      }
      setOtp({ ...otp, error: error?.message || 'Transfer failed' });
    } finally {
      setIsTransferring(false);
    }
  };

  // Resending is the first call over again; the new session replaces the old.
  const handleOtpResend = async () => {
    if (!otp || otpResending || isTransferring) return;
    setOtpResending(true);
    try {
      const result = await transferMutation.mutateAsync(otp.params);
      if (result.otp_required) {
        setOtp({
          params: otp.params,
          session: result.otp_session,
          phoneNumber: result.phone_number ?? otp.phoneNumber,
          spent: false,
        });
        return;
      }
      showSuccess(result.message || `Successfully transferred ${selectedTickets.length} ticket(s)`);
      setOtp(null);
      onClose();
    } catch (error: any) {
      setOtp({ ...otp, error: error?.message || 'Could not send a new code. Try again.' });
    } finally {
      setOtpResending(false);
    }
  };

  if (!open) return null;

  // The code step stands alone: the AXS transfer is already half-made, and
  // nothing in the picker below can change now without starting over.
  if (otp) {
    return (
      <Flex align="center" justify="center" fixed inset bg="black/80" backdrop zIndex={1000}>
        <Card
          fullWidth
          maxWidth="md"
          shadow
          animate="slideUp"
          bg="dark"
          padding="lg"
          hover={false}
          onClick={(e) => e.stopPropagation()}
        >
          <AxsTransferOtpPrompt
            phoneNumber={otp.phoneNumber}
            submitting={isTransferring}
            resending={otpResending}
            spent={otp.spent}
            error={otp.error}
            onSubmit={handleOtpSubmit}
            onResend={handleOtpResend}
            onCancel={() => {
              setOtp(null);
              onClose();
            }}
          />
        </Card>
      </Flex>
    );
  }

  // Get selected ticket objects for display
  const selectedTicketObjects = tickets.filter((t) =>
    selectedTickets.includes(t.external_ticket_id)
  );

  // Get platform and event info from first selected ticket
  const firstSelectedTicket = selectedTicketObjects[0];
  const platform = firstSelectedTicket
    ? (firstSelectedTicket.external_platform_connection?.platform || firstSelectedTicket.platform || '').toUpperCase()
    : '';
  const isDice = platform === 'DICE';
  const isAxs = platform === 'AXS';

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="lg"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="heading" mb={2}>
          Transfer Multiple Tickets
        </Text>
        <Text variant="muted" mb={4}>
          Select tickets from the same event to transfer together
        </Text>

        {/* Ticket Selection */}
        <div className="mb-4 max-h-64 overflow-y-auto">
          {tickets.map((ticket) => {
            const isSelected = selectedTickets.includes(ticket.external_ticket_id);
            const eventInfo = typeof ticket.event === 'object' ? ticket.event as any : null;
            const eventName = eventInfo?.name || ticket.event_name || 'Unknown Event';
            const venueName = eventInfo?.venue?.name || ticket.venue_name || '';
            const ticketPlatform = (ticket.external_platform_connection?.platform || ticket.platform || '').toUpperCase();

            return (
              <label
                key={ticket.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all mb-2 ${
                  isSelected
                    ? 'bg-blue-500/10 border-blue-500/30'
                    : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleTicketToggle(ticket.external_ticket_id)}
                  className="w-4 h-4"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{eventName}</span>
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-white/10 text-white/60">
                      {ticketPlatform}
                    </span>
                  </div>
                  {venueName && (
                    <span className="text-xs text-white/40">{venueName}</span>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {/* Info note */}
        {selectedTickets.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-blue-400 text-sm">
              ℹ️ Selected {selectedTickets.length} ticket(s) from {platform}. All tickets must be from the same platform and event.
            </p>
          </div>
        )}

        {/* Transfer Type Selection for DICE */}
        {selectedTickets.length > 0 && isDice && (
          <div className="mb-4 p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
            <p className="text-sm text-white/60 mb-2">Transfer by:</p>
            <Flex gap={2}>
              <Button
                type="button"
                variant={transferType === 'email' ? 'primary' : 'secondary'}
                onClick={() => setTransferType('email')}
                size="sm"
              >
                Email
              </Button>
              <Button
                type="button"
                variant={transferType === 'phone' ? 'primary' : 'secondary'}
                onClick={() => setTransferType('phone')}
                size="sm"
              >
                Phone Number
              </Button>
            </Flex>
          </div>
        )}

        {/* Recipient Input */}
        {selectedTickets.length > 0 && (
          <>
            {(!isDice || transferType === 'email') && (
              <Flex direction="col" mb={4}>
                <Input
                  type="email"
                  label="Recipient Email"
                  placeholder="recipient@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                />
                <Text variant="muted" mt={1}>
                  {isAxs
                    ? 'Transfer will only work by email for AXS'
                    : isDice
                      ? 'Recipient must have a connected DICE account'
                      : 'Transfer will be sent to this email address'}
                </Text>
              </Flex>
            )}

            {isDice && transferType === 'phone' && (
              <Flex direction="col" mb={4}>
                <Input
                  type="tel"
                  label="Recipient Phone Number"
                  placeholder="+1 (555) 123-4567"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                />
                <Text variant="muted" mt={1}>
                  Must add country code (e.g. +1) before the phone number
                </Text>
              </Flex>
            )}

            {isAxs && (
              <Flex gap={3} mb={4}>
                <Flex direction="col" grow>
                  <Input
                    type="text"
                    label="First Name"
                    placeholder="John"
                    value={recipientFirstName}
                    onChange={(e) => setRecipientFirstName(e.target.value)}
                  />
                </Flex>
                <Flex direction="col" grow>
                  <Input
                    type="text"
                    label="Last Name"
                    placeholder="Doe"
                    value={recipientLastName}
                    onChange={(e) => setRecipientLastName(e.target.value)}
                  />
                </Flex>
              </Flex>
            )}
          </>
        )}

        {/* Actions */}
        <Flex justify="end" gap={2}>
          <Button variant="secondary" onClick={onClose} disabled={isTransferring}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleTransfer}
            disabled={
              isTransferring ||
              selectedTickets.length === 0 ||
              (isDice && transferType === 'email' && !recipientEmail) ||
              (isDice && transferType === 'phone' && !recipientPhone) ||
              (isAxs && (!recipientEmail || !recipientFirstName || !recipientLastName)) ||
              (!isDice && !isAxs && !recipientEmail)
            }
          >
            {isTransferring ? 'Transferring...' : `Transfer ${selectedTickets.length} Ticket(s)`}
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
};
